import { createContext, useContext, useEffect } from "react";
import { Link as RouterLink, NavLink as RouterNavLink, useLocation, useNavigate } from "react-router-dom";

const en = {
  nav: { home: "Home", inventory: "Inventory", dismantling: "Dismantling", machinery: "Machinery", about: "About", contact: "Contact" },
  common: { callUs: "Call us", details: "Details", enquire: "Enquire", viewAll: "View full inventory", from: "from", loading: "Loading…", back: "Back to inventory" },
  hero: {
    kicker: "KATACO · Șoseaua Unirii 185, Balotesti",
    l1: "Curated machines.",
    l2: "Honest prices.",
    l3: "Zero compromise.",
    sub: "Premium used cars, commercial vans, heavy trucks and trailers — inspected, verified and ready to work from day one.",
    cta1: "Browse inventory",
    cta2: "Book a visit",
  },
  marquee: ["TRUSTED DEALERSHIP", "VERIFIED MILEAGE", "COMMERCIAL TRUCKS", "PREMIUM CARS", "FLEET SOURCING", "FAST REGISTRATION"],
  cats: {
    title: "Every machine has its place",
    sub: "From city hatchbacks to 40-tonne tractor units — one yard, every class.",
    explore: "Explore",
    cars: "Cars", vans: "Vans", trucks: "Trucks", trailers: "Trailers", forklifts: "Forklifts",
  },
  featured: { title: "Handpicked this week", sub: "A rotating selection of the strongest stock on the yard." },
  why: {
    title: "Why KATACO",
    sub: "The standard we hold every vehicle to before it earns a price tag.",
    i1t: "120-point inspection", i1d: "Full mechanical, electrical and structural check before any vehicle reaches the floor.",
    i2t: "European sourcing", i2d: "On-demand import from German, Dutch and Belgian fleets with documented service history.",
    i3t: "Leasing & finance", i3d: "Flexible financing for companies and individuals — approval in as little as 24 hours.",
    i4t: "Registration handled", i4d: "RAR, ITP and plates managed for you. Drive away the same day.",
  },
  stats: { stock: "Vehicles in stock", years: "Years on the market", delivered: "Vehicles delivered", satisfaction: "Client satisfaction" },
  cta: { title: "Can't find it? We'll source it.", sub: "Tell us the budget, body type and mileage — our buyers will locate it within days.", btn: "Send an enquiry" },
  inv: {
    title: "Inventory", sub: "Every vehicle inspected, priced and ready to leave the yard.",
    search: "Search make or model…", allCats: "All categories", allBrands: "All brands",
    results: "vehicles", empty: "No vehicles match your filters.", reset: "Reset filters",
    tabStock: "In stock", tabSold: "Sold archive", offersOnly: "Offers only",
    sort: { newest: "Newest first", "price-asc": "Price: low to high", "price-desc": "Price: high to low", "year-desc": "Youngest first", "mileage-asc": "Lowest mileage" },
  },
  status: { available: "Available", reserved: "Reserved", sold: "Sold" },
  offer: { badge: "SPECIAL OFFER" },
  det: {
    specs: "Specification", year: "Year", mileage: "Mileage", gearbox: "Gearbox", drivetrain: "Drivetrain", fuel: "Fuel", engine: "Engine", power: "Power", emission: "Emission", color: "Colour", category: "Category",
    finance: "Estimate your financing", deposit: "Deposit", term: "Term", monthly: "per month", months: "months",
    enquireTitle: "Enquire about this vehicle", related: "Similar vehicles", call: "Call sales",
    km: "km", features: "Features & equipment",
  },
  form: {
    name: "Full name", email: "Email", phone: "Phone", message: "Message",
    messagePh: "Tell us what you're looking for…", send: "Send enquiry", sending: "Sending…",
    sent: "Enquiry sent — we'll call you back shortly.", error: "Could not send your enquiry. Please call us instead.",
  },
  contact: {
    title: "Talk to us", sub: "Visit the yard on Șoseaua Unirii 185, Balotesti or send a message — we answer within one working day.",
    phone: "Call us", email: "Write to us", addr: "Find us", hours: "Working hours",
    hoursVal: "Mon – Fri 09:00 – 17:00 · Sat 10:00 – 15:00", formTitle: "Send an enquiry",
    faqTitle: "Common questions",
    faq: [
      { q: "Can I reserve a vehicle before visiting?", a: "Yes — call us or send an enquiry and we'll hold the vehicle for you until your visit." },
      { q: "Do you offer financing for companies?", a: "Yes — we work with several leasing partners and prepare financing offers for companies and individuals on request." },
      { q: "Can I inspect the vehicle before buying?", a: "Absolutely — bring your own mechanic, and the vehicle's inspection report is available on request." },
    ],
  },
  about: {
    title: "The KATACO standard",
    sub: "A family dealership built on one rule: never sell a vehicle we wouldn't drive ourselves.",
    p1: "KATACO started with three vans and a phone number. Fifteen years later the yard holds cars, commercial vehicles, heavy trucks, trailers and forklifts — every one of them inspected, history-checked and priced to move.",
    p2: "We buy from documented European fleets, reject more than we accept, and put every survivor through a 120-point protocol before it earns a spot on the yard.",
    inspTitle: "The 120-point protocol",
    inspSub: "Engine, transmission, chassis, electrics, body and paperwork — nothing ships without passing all six blocks.",
    srvTitle: "More than a dealership",
    srv: [
      { t: "Sourcing on demand", d: "Tell us the spec — we find it in Europe and deliver it registered." },
      { t: "Consignment sales", d: "We sell your vehicle from our yard and handle every viewing." },
      { t: "Trade-in & buy-back", d: "Your current vehicle counts toward your next one, on the spot." },
      { t: "Transport & delivery", d: "Nationwide delivery and export paperwork for EU buyers." },
      { t: "Car insurance", d: "RCA and CASCO policies arranged on the spot before you leave." },
      { t: "Vehicle documents", d: "Every document prepared, checked and handed over complete." },
      { t: "Vehicle registration", d: "RAR, ITP and plates handled — drive away registered." },
    ],
  },
  admin: {
    login: "Admin sign in", loginSub: "Manage incoming enquiries.",
    email: "Email", password: "Password", signIn: "Sign in", signingIn: "Signing in…", logout: "Sign out",
    dash: "Enquiries", total: "Total", new: "New", contacted: "Contacted", closed: "Closed",
    all: "All", name: "Name", contactCol: "Contact", vehicle: "Vehicle", date: "Date", statusCol: "Status",
    empty: "No enquiries yet.", open: "Open", messageCol: "Message",
    tabEnquiries: "Enquiries", tabVehicles: "Vehicles", tabParts: "Parts",
    addPart: "Add part", savedPart: "Part saved.", deletedPart: "Part deleted.", partsEmpty: "No parts yet.",
    fFuelEn: "Fuel (EN)", fFuelRo: "Fuel (RO)", fDescEn: "Description (EN)", fDescRo: "Description (RO)",
    addVehicle: "Add vehicle", editVehicle: "Edit", deleteVehicle: "Delete",
    deleteConfirm: "Delete this vehicle permanently?", markSold: "Mark sold", markAvailable: "Mark available",
    save: "Save", saving: "Saving…", cancel: "Cancel",
    savedVehicle: "Vehicle saved.", deletedVehicle: "Vehicle deleted.",
    uploaded: "Photos uploaded.", photos: "Photos", uploadPhotos: "Upload photos", uploading: "Uploading…",
    addImageUrl: "Paste image URL", add: "Add", mainPhoto: "Main", exportCsv: "Export CSV",
    fTitle: "Title", fBrand: "Brand", fCategory: "Category", fPrice: "Price (€)", fYear: "Year",
    fMileage: "Mileage (km)", fGearbox: "Gearbox", fDrivetrain: "Drivetrain", fFuel: "Fuel", fEngine: "Engine (cmc)", fPower: "Power",
    fEmission: "Emission", fColor: "Colour", fImage: "Main image URL",
    fImages: "Extra image URLs (one per line)", fStatus: "Status", fFeatured: "Featured on homepage",
    fOffer: "Special offer (shows ribbon)",
    fDescription: "Features & equipment (one per line)",
  },
  gallery: { photos: "photos" },
  share: { label: "Share this vehicle", copy: "Copy link", copied: "Link copied." },
  wa: { chat: "Chat on WhatsApp" },
  dz: {
    title: "Dismantling & Parts",
    sub: "Vehicles currently being dismantled on the yard — every part checked, fairly priced and ready for pickup or delivery.",
    onRequest: "Price on request",
    enquire: "Enquire about parts",
    prefill: "Parts enquiry for",
    h1t: "Tell us the part", h1d: "Call or send an enquiry naming the vehicle and the part you need — photos help.",
    h2t: "We check stock", h2d: "We confirm availability, condition and price within one working day.",
    h3t: "Pickup or delivery", h3d: "Collect from Șoseaua Unirii 185, Balotesti or we ship anywhere in Romania.",
    note: "Financial inquiries are welcome — reach out through the contact page and its form, and we'll reply with pricing and payment details.",
    noteLink: "Go to the contact page",
  },
  seo: {
    homeTitle: "KATACO — Verified Used Cars, Vans & Trucks in Balotesti",
    homeDesc: "Inspected used cars, vans, trucks, trailers and machinery for sale in Balotesti, Ilfov, plus quality dismantled parts. Fair prices, honest stock.",
    invTitle: "Inventory — KATACO Used Vehicles",
    invDesc: "Browse every inspected car, van, truck, trailer and machine in stock at KATACO, Balotesti — filter by category, brand and price.",
    dzTitle: "Auto Dismantling & Used Parts — KATACO",
    dzDesc: "Vehicles currently dismantled at KATACO — tested used parts for cars and vans on request. Enquire online or call +40 723 250 260.",
    aboutTitle: "About KATACO — The KATACO Standard",
    aboutDesc: "A family dealership in Balotesti, Ilfov built on one rule: never sell a vehicle we wouldn't drive ourselves. 120-point inspection on every vehicle.",
    contactTitle: "Contact KATACO — Balotesti, Ilfov",
    contactDesc: "Visit KATACO on Șoseaua Unirii 185, Balotesti, call +40 723 250 260 or send an enquiry — we answer within one working day.",
    vehDescSuffix: "at KATACO Balotesti — inspected and ready. Enquire online or call +40 723 250 260.",
    machTitle: "Machinery & Utilities — KATACO",
    machDesc: "Concrete pumps, cranes, excavators and generators — inspected industrial machinery at KATACO Balotesti, with real working footage. Enquire online or call +40 723 250 260.",
  },
  mach: {
    title: "Machinery & Utilities",
    sub: "Beyond vehicles: concrete pumps, cranes, excavators and power generators — industrial equipment inspected with the same 120-point rigour as everything else on the yard, plus the know-how to put it to work.",
    videosTitle: "See the equipment at work",
    videosSub: "Real footage from KATACO Worldwide operations — no studio, no staging.",
    v1t: "Concrete Pump — Full Working Cycle",
    v1d: "Pipeline assembly, mixing, pumping to the construction point and the full line-cleaning procedure — a complete cycle of our concrete pumping system on a live site.",
    v2t: "Mixer Pump Truck — Pre-Operation Checks",
    v2d: "The complete pre-operation routine for our concrete mixer pump truck: stabilisation, fluid levels, electrical systems and full function tests before work begins.",
    stockTitle: "Machinery currently in stock",
    stockSub: "Industrial equipment on the yard, ready for inspection.",
    ctaText: "Looking for a specific machine? Tell us the spec — we source industrial equipment on request and deliver it ready to work.",
  },
  srvx: {
    kicker: "KEY SERVICES",
    title: "Beyond the sale",
    sub: "Insurance, documents, registration and consignment — handled end to end by the same team that hands you the keys.",
    i1t: "Car insurance", i1d: "RCA and CASCO policies arranged on the spot, paperwork issued before you leave the yard.",
    i2t: "Vehicle documents", i2d: "Every document your vehicle needs — prepared, checked and handed over complete.",
    i3t: "Vehicle registration", i3d: "RAR, ITP and number plates handled for you — drive away registered the same day.",
    i4t: "Consignment sales", i4d: "We sell your vehicle from our yard and manage every viewing, call and signature.",
  },
  footer: {
    tagline: "Curated commercial & premium used vehicles in Romania.",
    nav: "Navigate", cats: "Vehicles", contact: "Contact",
    rights: "All rights reserved.", adminLink: "Admin",
  },
};

const ro = {
  nav: { home: "Acasă", inventory: "Parc Auto", dismantling: "Dezmembrări", machinery: "Utilaje", about: "Despre Noi", contact: "Contact" },
  common: { callUs: "Sună-ne", details: "Detalii", enquire: "Solicită ofertă", viewAll: "Vezi tot stocul", from: "de la", loading: "Se încarcă…", back: "Înapoi la stoc" },
  hero: {
    kicker: "KATACO · Șoseaua Unirii 185, Balotesti",
    l1: "Mașini selectate.",
    l2: "Prețuri corecte.",
    l3: "Zero compromisuri.",
    sub: "Autoturisme premium rulate, autoutilitare, camioane și remorci — verificate, inspectate și gata de drum din prima zi.",
    cta1: "Vezi stocul",
    cta2: "Programează o vizită",
  },
  marquee: ["DEALER DE ÎNCREDERE", "KILOMETRAJ VERIFICAT", "CAMIOANE COMERCIALE", "MAȘINI PREMIUM", "ACHIZIȚII FLOTĂ", "ÎNMATRICULARE RAPIDĂ"],
  cats: {
    title: "Fiecare mașină își are locul ei",
    sub: "De la hatchback-uri de oraș la capete tractor de 40 de tone — o singură curte, toate clasele.",
    explore: "Explorează",
    cars: "Autoturisme", vans: "Autoutilitare", trucks: "Camioane", trailers: "Remorci", forklifts: "Stivuitoare",
  },
  featured: { title: "Selecția săptămânii", sub: "O selecție rotativă a celor mai bune vehicule din curte." },
  why: {
    title: "De ce KATACO",
    sub: "Standardul pe care îl îndeplinește fiecare vehicul înainte să primească un preț.",
    i1t: "Inspecție în 120 de puncte", i1d: "Verificare completă mecanică, electrică și structurală înainte ca orice vehicul să ajungă în curte.",
    i2t: "Import european", i2d: "Import la cerere din flote din Germania, Olanda și Belgia, cu istoric de service documentat.",
    i3t: "Leasing și finanțare", i3d: "Finanțare flexibilă pentru companii și persoane fizice — aprobare în doar 24 de ore.",
    i4t: "Înmatriculare inclusă", i4d: "RAR, ITP și plăcuțe gestionate de noi. Pleci la volan în aceeași zi.",
  },
  stats: { stock: "Vehicule în stoc", years: "Ani pe piață", delivered: "Vehicule livrate", satisfaction: "Clienți mulțumiți" },
  cta: { title: "Nu găsești? Îl aducem noi.", sub: "Spune-ne bugetul, caroseria și kilometrajul — cumpărătorii noștri îl găsesc în câteva zile.", btn: "Trimite o cerere" },
  inv: {
    title: "Parc Auto", sub: "Fiecare vehicul inspectat, evaluat și gata de plecare.",
    search: "Caută marcă sau model…", allCats: "Toate categoriile", allBrands: "Toate mărcile",
    results: "vehicule", empty: "Niciun vehicul nu corespunde filtrelor.", reset: "Resetează filtrele",
    tabStock: "În stoc", tabSold: "Arhivă vândute", offersOnly: "Doar oferte",
    sort: { newest: "Cele mai noi", "price-asc": "Preț: crescător", "price-desc": "Preț: descrescător", "year-desc": "Cele mai noi ca an", "mileage-asc": "Kilometraj minim" },
  },
  status: { available: "Disponibil", reserved: "Rezervat", sold: "Vândut" },
  offer: { badge: "OFERTĂ" },
  det: {
    specs: "Specificații", year: "An fabricație", mileage: "Kilometraj", gearbox: "Cutie de viteze", drivetrain: "Transmisie", fuel: "Combustibil", engine: "Motor", power: "Putere", emission: "Normă poluare", color: "Culoare", category: "Categorie",
    finance: "Estimează finanțarea", deposit: "Avans", term: "Perioadă", monthly: "pe lună", months: "luni",
    enquireTitle: "Solicită ofertă pentru acest vehicul", related: "Vehicule similare", call: "Sună vânzări",
    km: "km", features: "Dotări și echipare",
  },
  form: {
    name: "Nume complet", email: "Email", phone: "Telefon", message: "Mesaj",
    messagePh: "Spune-ne ce cauți…", send: "Trimite cererea", sending: "Se trimite…",
    sent: "Cerere trimisă — te sunăm noi în cel mai scurt timp.", error: "Cererea nu a putut fi trimisă. Te rugăm să ne suni.",
  },
  contact: {
    title: "Hai să vorbim", sub: "Vizitează-ne pe Șoseaua Unirii 185, Balotesti sau trimite un mesaj — răspundem într-o zi lucrătoare.",
    phone: "Sună-ne", email: "Scrie-ne", addr: "Găsește-ne", hours: "Program",
    hoursVal: "Lun – Vin 09:00 – 17:00 · Sâm 10:00 – 15:00", formTitle: "Trimite o cerere",
    faqTitle: "Întrebări frecvente",
    faq: [
      { q: "Pot rezerva un vehicul înainte de vizită?", a: "Da — sună-ne sau trimite o cerere și îți ținem vehiculul până la vizită." },
      { q: "Oferiți finanțare pentru companii?", a: "Da — colaborăm cu mai mulți parteneri de leasing și pregătim oferte de finanțare pentru companii și persoane fizice, la cerere." },
      { q: "Pot inspecta vehiculul înainte de cumpărare?", a: "Desigur — adu-ți propriul mecanic, iar raportul de inspecție al vehiculului este disponibil la cerere." },
    ],
  },
  about: {
    title: "Standardul KATACO",
    sub: "Un dealer de familie construit pe o singură regulă: nu vindem un vehicul pe care nu l-am conduce noi înșine.",
    p1: "KATACO a început cu trei utilitare și un număr de telefon. Cincisprezece ani mai târziu, curtea găzduiește autoturisme, autoutilitare, camioane, remorci și stivuitoare — toate inspectate, cu istoric verificat și prețuri corecte.",
    p2: "Cumpărăm din flote europene documentate, refuzăm mai mult decât acceptăm și trecem fiecare supraviețuitor printr-un protocol de 120 de puncte înainte să ajungă în curte.",
    inspTitle: "Protocolul de 120 de puncte",
    inspSub: "Motor, transmisie, șasiu, electrice, caroserie și acte — nimic nu pleacă fără să treacă toate cele șase blocuri.",
    srvTitle: "Mai mult decât un dealer",
    srv: [
      { t: "Achiziție la cerere", d: "Spune-ne specificația — o găsim în Europa și o livrăm înmatriculată." },
      { t: "Vânzare în consignație", d: "Îți vindem vehiculul din curtea noastră și gestionăm fiecare vizionare." },
      { t: "Trade-in și buy-back", d: "Vehiculul tău actual contează la următorul, pe loc." },
      { t: "Transport și livrare", d: "Livrare națională și acte de export pentru cumpărătorii din UE." },
      { t: "Asigurări auto", d: "Polițe RCA și CASCO emise pe loc, înainte să pleci." },
      { t: "Acte auto", d: "Toate documentele pregătite, verificate și predate complet." },
      { t: "Înmatriculări", d: "RAR, ITP și plăcuțe gestionate — pleci înmatriculat." },
    ],
  },
  admin: {
    login: "Autentificare admin", loginSub: "Gestionează cererile primite.",
    email: "Email", password: "Parolă", signIn: "Autentificare", signingIn: "Se autentifică…", logout: "Ieșire",
    dash: "Cereri", total: "Total", new: "Noi", contacted: "Contactate", closed: "Închise",
    all: "Toate", name: "Nume", contactCol: "Contact", vehicle: "Vehicul", date: "Dată", statusCol: "Status",
    empty: "Nicio cerere încă.", open: "Deschide", messageCol: "Mesaj",
    tabEnquiries: "Cereri", tabVehicles: "Vehicule", tabParts: "Piese",
    addPart: "Adaugă piesă", savedPart: "Piesă salvată.", deletedPart: "Piesă ștearsă.", partsEmpty: "Nicio piesă încă.",
    fFuelEn: "Combustibil (EN)", fFuelRo: "Combustibil (RO)", fDescEn: "Descriere (EN)", fDescRo: "Descriere (RO)",
    addVehicle: "Adaugă vehicul", editVehicle: "Editează", deleteVehicle: "Șterge",
    deleteConfirm: "Ștergi definitiv acest vehicul?", markSold: "Marchează vândut", markAvailable: "Marchează disponibil",
    save: "Salvează", saving: "Se salvează…", cancel: "Anulează",
    savedVehicle: "Vehicul salvat.", deletedVehicle: "Vehicul șters.",
    uploaded: "Fotografii încărcate.", photos: "Fotografii", uploadPhotos: "Încarcă fotografii", uploading: "Se încarcă…",
    addImageUrl: "Lipește URL imagine", add: "Adaugă", mainPhoto: "Principală", exportCsv: "Exportă CSV",
    fTitle: "Titlu", fBrand: "Marcă", fCategory: "Categorie", fPrice: "Preț (€)", fYear: "An",
    fMileage: "Kilometraj (km)", fGearbox: "Cutie de viteze", fDrivetrain: "Transmisie", fFuel: "Combustibil", fEngine: "Cilindree (cmc)", fPower: "Putere",
    fEmission: "Normă poluare", fColor: "Culoare", fImage: "URL imagine principală",
    fImages: "URL-uri imagini extra (câte una pe rând)", fStatus: "Status", fFeatured: "Recomandat pe prima pagină",
    fOffer: "Ofertă specială (afișează panglica)",
    fDescription: "Dotări și echipare (câte una pe rând)",
  },
  gallery: { photos: "fotografii" },
  share: { label: "Trimite vehiculul mai departe", copy: "Copiază link", copied: "Link copiat." },
  wa: { chat: "Scrie-ne pe WhatsApp" },
  dz: {
    title: "Dezmembrări Auto",
    sub: "Mașini aflate în dezmembrare în curte — fiecare piesă verificată, la preț corect, gata de ridicare sau livrare.",
    onRequest: "Preț la cerere",
    enquire: "Solicită piese",
    prefill: "Cerere piese pentru",
    h1t: "Spune-ne piesa", h1d: "Sună-ne sau trimite o cerere cu mașina și piesa de care ai nevoie — pozele ajută.",
    h2t: "Verificăm stocul", h2d: "Confirmăm disponibilitatea, starea și prețul într-o zi lucrătoare.",
    h3t: "Ridicare sau livrare", h3d: "Ridici de pe Șoseaua Unirii 185, Balotesti sau livrăm oriunde în România.",
    note: "Întrebările financiare sunt binevenite — contactează-ne prin pagina de contact și formularul acesteia, iar noi îți răspundem cu prețuri și detalii de plată.",
    noteLink: "Mergi la pagina de contact",
  },
  seo: {
    homeTitle: "KATACO — Mașini, Utilitare și Camioane Rulate Verificate",
    homeDesc: "Autoturisme, autoutilitare, camioane, remorci și utilaje verificate, de vânzare în Balotesti, Ilfov, plus piese din dezmembrări. Prețuri corecte, stoc real.",
    invTitle: "Parc Auto — Vehicule Rulate KATACO",
    invDesc: "Vezi tot stocul KATACO din Balotesti — mașini, utilitare, camioane, remorci și utilaje inspectate. Filtrează după categorie, marcă și preț.",
    dzTitle: "Dezmembrări Auto & Piese Second — KATACO",
    dzDesc: "Mașini aflate în dezmembrare la KATACO — piese second verificate pentru autoturisme și utilitare, la cerere. Trimite o cerere sau sună la +40 723 250 260.",
    aboutTitle: "Despre KATACO — Standardul KATACO",
    aboutDesc: "Un dealer de familie din Balotesti, Ilfov, cu o singură regulă: nu vindem un vehicul pe care nu l-am conduce noi înșine. Inspecție în 120 de puncte.",
    contactTitle: "Contact KATACO — Balotesti, Ilfov",
    contactDesc: "Ne găsești pe Șoseaua Unirii 185, Balotesti, la +40 723 250 260 sau prin formular — răspundem într-o zi lucrătoare.",
    vehDescSuffix: "la KATACO Balotesti — inspectat și gata de drum. Trimite o cerere online sau sună la +40 723 250 260.",
    machTitle: "Utilaje și Echipamente — KATACO",
    machDesc: "Pompe de beton, macarale, excavatoare și generatoare — utilaje industriale verificate la KATACO Balotesti, cu imagini reale de funcționare. Trimite o cerere sau sună la +40 723 250 260.",
  },
  mach: {
    title: "Utilaje și Echipamente",
    sub: "Mai mult decât vehicule: pompe de beton, macarale, excavatoare și generatoare de curent — utilaje inspectate cu aceeași rigoare în 120 de puncte ca orice altceva din curte, plus know-how-ul necesar pentru a le pune în funcțiune.",
    videosTitle: "Vezi utilajele la lucru",
    videosSub: "Imagini reale din operațiunile KATACO Worldwide — fără studio, fără regie.",
    v1t: "Pompă de Beton — Ciclu Complet de Lucru",
    v1d: "Montarea conductelor, mixarea, pomparea până la punctul de turnare și procedura completă de curățare a liniei — un ciclu integral al sistemului nostru de pompare a betonului, pe un șantier real.",
    v2t: "Autopompă cu Malaxor — Verificări de Pre-Operare",
    v2d: "Rutina completă de pre-operare pentru autopompa cu malaxor: stabilizare, niveluri de fluide, sistem electric și teste complete de funcționare înainte de lucru.",
    stockTitle: "Utilaje aflate în stoc",
    stockSub: "Echipamente industriale din curte, gata de inspecție.",
    ctaText: "Cauți un anumit utilaj? Spune-ne specificația — aducem echipamente industriale la cerere și le livrăm gata de lucru.",
  },
  srvx: {
    kicker: "SERVICII CHEIE",
    title: "Dincolo de vânzare",
    sub: "Asigurări, acte, înmatriculări și consignație — gestionate complet de aceeași echipă care îți înmânează cheile.",
    i1t: "Asigurări auto", i1d: "Polițe RCA și CASCO emise pe loc, cu actele gata înainte să pleci din curte.",
    i2t: "Acte auto complete", i2d: "Toate documentele de care are nevoie vehiculul tău — pregătite, verificate și predate complet.",
    i3t: "Înmatriculări auto", i3d: "RAR, ITP și plăcuțe de înmatriculare gestionate de noi — pleci înmatriculat în aceeași zi.",
    i4t: "Vânzare în consignație", i4d: "Îți vindem mașina din curtea noastră și gestionăm fiecare vizionare, apel și semnătură.",
  },
  footer: {
    tagline: "Autovehicule rulate premium și utilitare verificate în România.",
    nav: "Navigare", cats: "Vehicule", contact: "Contact",
    rights: "Toate drepturile rezervate.", adminLink: "Admin",
  },
};

const dictionaries = { en, ro };
const LangCtx = createContext(null);

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isRo = location.pathname === "/ro" || location.pathname.startsWith("/ro/");
  const lang = isRo ? "ro" : "en";

  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem("kataco-lang", lang);
  }, [lang]);

  const setLang = (l) => {
    localStorage.setItem("kataco-lang", l);
    const bare = location.pathname.replace(/^\/ro(?=\/|$)/, "") || "/";
    if (l === "ro" && !isRo) navigate(`${bare === "/" ? "/ro" : `/ro${bare}`}${location.search}`);
    if (l === "en" && isRo) navigate(`${bare}${location.search}`);
  };

  const t = (key) => key.split(".").reduce((o, p) => (o != null ? o[p] : undefined), dictionaries[lang]) ?? key;
  return <LangCtx.Provider value={{ lang, setLang, t }}>{children}</LangCtx.Provider>;
}

export const useLang = () => useContext(LangCtx);

const withLangPrefix = (to, lang) => {
  if (lang === "ro" && typeof to === "string" && to.startsWith("/") && !to.startsWith("/ro")) {
    return `/ro${to === "/" ? "" : to}`;
  }
  return to;
};

export function Link({ to, ...props }) {
  const { lang } = useLang();
  return <RouterLink to={withLangPrefix(to, lang)} {...props} />;
}

export function NavLink({ to, ...props }) {
  const { lang } = useLang();
  const href = withLangPrefix(to, lang);
  return <RouterNavLink to={href} end={href === "/" || href === "/ro"} {...props} />;
}
